import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import FinancePageHeader from '../components/finance/FinancePageHeader';
import { useFinanceEmbedded } from '../hooks/useFinanceEmbedded';
import { loadMarginCalculatorData, saveMarginCalculatorData } from '../services/marginCalculatorService';
import PortalBackground from '../components/PortalBackground';

// Convert a stored decimal (e.g. 0.11) to a clean percent number for display,
// stripping floating-point noise like 11.000000000000002.
const toPct = (v) => Number((v * 100).toFixed(4));

// Reusable labeled number input with an optional $ prefix or % suffix.
const CalcInput = ({ darkMode, label, prefix, suffix, ...inputProps }) => (
  <div className={`rounded-xl border p-4 shadow-sm bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
    <label className={`block text-sm font-semibold mb-2 text-center ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{label}</label>
    <div className="relative rounded-md shadow-sm">
      {prefix && (
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{prefix}</span>
        </div>
      )}
      <input
        type="number"
        className={`block w-full rounded-md border py-2 ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'} focus:border-red-500 focus:ring-red-500 text-lg text-center ${
          darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
        }`}
        {...inputProps}
      />
      {suffix && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{suffix}</span>
        </div>
      )}
    </div>
  </div>
);

// overrideUserId lets an internal viewer (CRM Portal View) load/save a specific
// client's margin calculator data instead of their own.
const MarginCalculator = ({ embedded: embeddedProp, overrideUserId = null }) => {
  const embedded = useFinanceEmbedded(embeddedProp);
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  // The uid whose margin calculator data is read/written.
  const effectiveUid = overrideUserId || currentUser?.uid;

  // Guards so simply viewing a client (override) doesn't overwrite their data:
  // autosave only fires after the initial load, and the redundant save caused
  // by loading data into state is skipped.
  const didLoadRef = useRef(false);
  const skipNextSaveRef = useRef(false);

  // Loading and saving states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // Default state values
  const [inputs, setInputs] = useState({
    taxRate: 0.20,
    profitabilityDesire: 0.11,
    startingGPM: 0.30,
    overhead: 260000,
  });
  
  // Active tab for output display (annual, quarterly, monthly)
  const [activeTab, setActiveTab] = useState('annual');

  // Calculated values
  const [calculatedValues, setCalculatedValues] = useState({
    impliedEBT: 0,
    salesBreakeven: 0,
    salesToProfitGoal: 0,
  });

  // State for scenarios
  const [scenarios, setScenarios] = useState([]);

  // Load user data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!effectiveUid) return;

      try {
        setLoading(true);
        const data = await loadMarginCalculatorData(effectiveUid);
        setInputs(data.inputs || inputs);
        // Skip the autosave triggered by loading data into state.
        skipNextSaveRef.current = true;
        didLoadRef.current = true;
      } catch (error) {
        console.error('Error loading margin calculator data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [effectiveUid]);

  // Auto-save functionality
  const autoSave = async () => {
    if (!effectiveUid || loading) return;

    try {
      setSaving(true);
      await saveMarginCalculatorData({
        inputs,
        lastCalculated: new Date().toISOString()
      }, effectiveUid);
    } catch (error) {
      console.error('Error auto-saving margin calculator data:', error);
    } finally {
      setSaving(false);
    }
  };

  // Auto-save when inputs change
  useEffect(() => {
    if (!didLoadRef.current) return; // nothing loaded yet — don't save defaults
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    const timeoutId = setTimeout(() => {
      autoSave();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [inputs]);

  // Calculate scenarios whenever inputs change
  useEffect(() => {
    const impliedEBT = inputs.profitabilityDesire / (1 - inputs.taxRate);
    const salesBreakeven = inputs.overhead / inputs.startingGPM;
    const salesToProfitGoal = inputs.overhead / (inputs.startingGPM - impliedEBT);

    setCalculatedValues({
      impliedEBT,
      salesBreakeven,
      salesToProfitGoal,
    });

    // Generate scenarios
    const newScenarios = [];
    const baseGPM = inputs.startingGPM;
    const increment = 0.005; // Fixed at 0.5%

    // Generate 5 scenarios below and 5 above the starting GPM
    for (let i = -5; i <= 5; i++) {
      const gpm = baseGPM + (i * increment);
      if (gpm <= 0) continue; // Skip negative GPM
      
      const scenario = calculateScenario(gpm, inputs.overhead, impliedEBT, inputs.taxRate);
      newScenarios.push(scenario);
    }

    setScenarios(newScenarios);
  }, [inputs]);

  // Function to calculate a single scenario
  const calculateScenario = (gpm, overhead, impliedEBT, taxRate) => {
    // Avoid division by zero or negative values
    if (gpm <= impliedEBT) {
      return {
        gpm,
        salesBreakeven: overhead / gpm,
        salesToProfitGoal: Infinity, // Cannot achieve profit goal
        annual: { 
          sales: 0, 
          gpm: 0, 
          grossProfit: 0, 
          overhead: overhead, 
          ebt: 0, 
          taxes: 0, 
          netProfit: 0, 
          profitMargin: 0 
        },
        quarterly: { sales: 0, grossProfit: 0, overhead: 0, ebt: 0, taxes: 0, netProfit: 0 },
        monthly: { sales: 0, grossProfit: 0, overhead: 0, ebt: 0, taxes: 0, netProfit: 0 },
      };
    }

    const salesBreakeven = overhead / gpm;
    const salesToProfitGoal = overhead / (gpm - impliedEBT);
    
    // Annual calculations
    const annualSales = salesToProfitGoal;
    const annualGrossProfit = annualSales * gpm;
    const annualEBT = annualGrossProfit - overhead;
    const annualTaxes = annualEBT * taxRate;
    const annualNetProfit = annualEBT - annualTaxes;
    const profitMargin = annualNetProfit / annualSales;
    
    // Quarterly and monthly are just divisions of annual
    const quarterly = {
      sales: annualSales / 4,
      grossProfit: annualGrossProfit / 4,
      overhead: overhead / 4,
      ebt: annualEBT / 4,
      taxes: annualTaxes / 4,
      netProfit: annualNetProfit / 4,
    };
    
    const monthly = {
      sales: annualSales / 12,
      grossProfit: annualGrossProfit / 12,
      overhead: overhead / 12,
      ebt: annualEBT / 12,
      taxes: annualTaxes / 12,
      netProfit: annualNetProfit / 12,
    };
    
    return {
      gpm,
      salesBreakeven,
      salesToProfitGoal,
      annual: {
        sales: annualSales,
        gpm,
        grossProfit: annualGrossProfit,
        overhead,
        ebt: annualEBT,
        taxes: annualTaxes,
        netProfit: annualNetProfit,
        profitMargin,
      },
      quarterly,
      monthly,
    };
  };

  // Handler for input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let parsedValue = parseFloat(value);
    
    // Convert percentage inputs to decimal
    if (['taxRate', 'profitabilityDesire', 'startingGPM'].includes(name)) {
      parsedValue = parsedValue / 100;
    }
    
    setInputs(prev => ({ ...prev, [name]: parsedValue }));
  };

  // Format numbers as currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };
  
  // Format numbers as percentages
  const formatPercent = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  };

  // Find the scenario closest to the starting GPM
  const currentScenario = scenarios.find(s => Math.abs(s.gpm - inputs.startingGPM) < 0.0001) ||
    (scenarios.length > 0 ? scenarios[Math.floor(scenarios.length / 2)] : null);

  // Result tiles for the selected period (Break-Even is annual ÷ period; profit
  // margin is period-invariant so it always uses the annual figure).
  const periodDivisor = activeTab === 'quarterly' ? 4 : activeTab === 'monthly' ? 12 : 1;
  const period = currentScenario ? currentScenario[activeTab] : null;
  const outputCards = currentScenario ? [
    { label: 'Break-Even Sales', value: formatCurrency(currentScenario.salesBreakeven / periodDivisor) },
    { label: 'Sales to Profit', value: formatCurrency(period.sales) },
    { label: 'Gross Profit', value: formatCurrency(period.grossProfit) },
    { label: 'Overhead', value: formatCurrency(period.overhead) },
    { label: 'Earnings Before Taxes', value: formatCurrency(period.ebt) },
    { label: 'Taxes', value: formatCurrency(period.taxes) },
    { label: 'Net Profit', value: formatCurrency(period.netProfit) },
    { label: 'Profit Margin', value: formatPercent(currentScenario.annual.profitMargin) },
  ] : [];

  return (
    <div className={`text-gray-900 dark:text-white ${embedded ? '' : 'relative p-6 max-w-6xl mx-auto min-h-screen'}`}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? '' : 'relative'}>
      {!embedded && <FinancePageHeader title="Margin Calculator" />}

      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-3 rounded-md text-center ${
          message.includes('Error') 
            ? 'bg-red-100 text-red-700 border border-red-300' 
            : 'bg-green-100 text-green-700 border border-green-300'
        }`}>
          {message}
        </div>
      )}
      
      {/* Inputs Section — all inputs grouped together */}
      <div className="mb-8">
        <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-700'}`}>Inputs</h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
          <CalcInput darkMode={darkMode} label="Target Gross Profit Margin" name="startingGPM" value={toPct(inputs.startingGPM)} onChange={handleInputChange} suffix="%" placeholder="30" min="0" max="100" step="0.5" />
          <CalcInput darkMode={darkMode} label="Assumed Tax Rate" name="taxRate" value={toPct(inputs.taxRate)} onChange={handleInputChange} suffix="%" placeholder="20" min="0" max="100" step="0.1" />
          <CalcInput darkMode={darkMode} label="Profitability Desire" name="profitabilityDesire" value={toPct(inputs.profitabilityDesire)} onChange={handleInputChange} suffix="%" placeholder="11" min="0" max="100" step="0.1" />
          <CalcInput darkMode={darkMode} label="Overhead" name="overhead" value={inputs.overhead} onChange={handleInputChange} prefix="$" placeholder="260000" min="0" step="1000" />
        </div>
      </div>

      {/* Results Section with Annual / Quarterly / Monthly tabs */}
      {currentScenario && (
        <div className="mb-8">
          <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <nav className="flex justify-center space-x-8" aria-label="Tabs">
              {['Annual', 'Quarterly', 'Monthly'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab.toLowerCase())}
                  className={`${
                    activeTab === tab.toLowerCase()
                      ? 'border-red-500 text-red-600'
                      : darkMode
                        ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {outputCards.map((card) => (
              <div
                key={card.label}
                className={`rounded-xl border p-4 shadow-sm flex flex-col justify-center items-center text-center bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}
              >
                <div className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{card.label}</div>
                <div className="text-xl font-bold">{card.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table Section */}
      <div>
        <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-700'}`}>Scenario Analysis</h2>
        
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>GPM</th>
                <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Break-Even Sales</th>
                <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Sales to Profit Goal</th>
                <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Gross Profit</th>
                <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Overhead</th>
                <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>EBT</th>
                <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Taxes</th>
                <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Net Profit</th>
                <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Profit Margin</th>
              </tr>
            </thead>
            <tbody className={`${darkMode ? 'bg-gray-900' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {scenarios.map((scenario, index) => (
                <tr 
                  key={index} 
                  className={Math.abs(scenario.gpm - inputs.startingGPM) < 0.0001 ? (darkMode ? "bg-red-900/30" : "bg-red-50") : ""}
                >
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatPercent(scenario.gpm)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatCurrency(scenario.salesBreakeven)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {scenario.salesToProfitGoal === Infinity 
                      ? "N/A" 
                      : formatCurrency(scenario.salesToProfitGoal)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatCurrency(scenario.annual.grossProfit)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatCurrency(scenario.annual.overhead)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatCurrency(scenario.annual.ebt)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatCurrency(scenario.annual.taxes)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatCurrency(scenario.annual.netProfit)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatPercent(scenario.annual.profitMargin)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MarginCalculator; 